import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Avatar,
  Tabs,
  Tab,
  Card,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  IconButton,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhancedTableColumn,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { IConfirmation } from 'src/redux/confirmation/types';
import { OpenConfirmation } from 'src/redux/confirmation/confirmationReducer';
import { Helmet } from 'react-helmet-async';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import { DataService } from 'src/services/data-service';
import { IService } from '.';
import Loader from 'src/components/Loader';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import AddService from './add-service';

interface IServiceListing {
  id: string;
  name: string;
  description: string;
  iconUrl?: string;
  active: boolean;
  status: string;
}

type LanguageTab = 'en' | 'ar' | 'fr';

// Separate component for service icon with error handling
const ServiceIcon: React.FC<{ iconUrl?: string; name: string }> = ({ iconUrl, name }) => {
  const [imageError, setImageError] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // Properly encode the URL by first decoding any existing encoding, then re-encoding
  const getEncodedUrl = (url?: string): string => {
    if (!url) return '';

    try {
      // Split URL into parts to handle it properly
      const urlParts = url.split('/');
      const fileName = urlParts[urlParts.length - 1];
      const basePath = urlParts.slice(0, -1).join('/');

      // Encode only the filename part (which may have spaces and special chars)
      const encodedFileName = encodeURIComponent(decodeURIComponent(fileName));

      return `${basePath}/${encodedFileName}`;
    } catch (e) {
      // If decoding fails, just encode the whole thing
      console.error('Error encoding URL:', e);
      return url.replace(/ /g, '%20'); // At minimum, replace spaces
    }
  };

  const imageUrl = useMemo(() => getEncodedUrl(iconUrl), [iconUrl]);

  // Fetch image as blob to bypass Content-Disposition header
  useEffect(() => {
    console.log('ServiceIcon useEffect running:', { imageUrl, imageError, iconUrl });

    if (!imageUrl || imageError) {
      console.log('Skipping fetch - no URL or error:', { imageUrl, imageError });
      return;
    }

    let currentBlobUrl = '';

    const fetchImage = async () => {
      try {
        console.log('Fetching image from:', imageUrl);
        setLoading(true);
        const response = await fetch(imageUrl);

        console.log('Fetch response:', response.status, response.statusText);

        if (!response.ok) {
          console.error('Failed to fetch image:', response.status, response.statusText);
          setImageError(true);
          return;
        }

        const blob = await response.blob();
        console.log('Blob created:', blob.type, blob.size);
        const url = URL.createObjectURL(blob);
        console.log('Object URL created:', url);
        currentBlobUrl = url;
        setBlobUrl(url);
      } catch (error) {
        console.error('Error fetching image:', error);
        setImageError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchImage();

    // Cleanup blob URL on unmount or when imageUrl changes
    return () => {
      if (currentBlobUrl) {
        console.log('Cleaning up blob URL:', currentBlobUrl);
        URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [imageUrl, imageError]);

  const displayUrl = blobUrl || imageUrl;

  return (
    <Tooltip
      title={
        displayUrl && !imageError ? (
          <Box
            component="img"
            src={displayUrl}
            alt={name}
            sx={{
              maxWidth: 200,
              maxHeight: 200,
              objectFit: 'contain',
              backgroundColor: 'white',
              borderRadius: 1,
              p: 1
            }}
            onError={() => {
              console.error('Tooltip image failed:', displayUrl);
              setImageError(true);
            }}
          />
        ) : (
          'No icon available'
        )
      }
      placement="right"
    >
      <Avatar
        src={!imageError && displayUrl ? displayUrl : undefined}
        alt={name}
        sx={{
          width: 50,
          height: 50,
          bgcolor: imageError || !displayUrl || loading ? 'primary.light' : 'transparent',
          border: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          '& img': {
            objectFit: 'contain',
            backgroundColor: 'white',
            padding: '2px'
          }
        }}
        imgProps={{
          loading: 'lazy',
          onError: () => {
            console.error('Avatar image failed to load:', displayUrl);
            setImageError(true);
          }
        }}
      >
        {(imageError || !displayUrl || loading) && name?.charAt(0)?.toUpperCase()}
      </Avatar>
    </Tooltip>
  );
};

const ServiceListing: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [allData, setAllData] = useState<IService[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openAddService, setOpenAddService] = useState<boolean>(false);
  const [selectedService, setSelectedService] = useState<IService | null>(null);
  const [activeTab, setActiveTab] = useState<LanguageTab>('en');
  const [openExceptionDialog, setOpenExceptionDialog] = useState<boolean>(false);
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [exceptionDate, setExceptionDate] = useState<string>('');
  const [exceptionClosed, setExceptionClosed] = useState<boolean>(true);
  const [exceptionWindows, setExceptionWindows] = useState<{ start: string; end: string }[]>([
    { start: '09:00', end: '17:00' }
  ]);

  const activate = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to activate this service?',
      onSubmit: async () => {
        const result = await DataService.post('api/Service/activate?id=' + id, {});
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'Service activated successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to activate service', type: 'error' }));
        }
      }
    };

    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const deactivate = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to deactivate this service?',
      onSubmit: async () => {
        const result = await DataService.post(
          'api/Service/deactivate?id=' + id,
          {}
        );
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'Service deactivated successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to deactivate service', type: 'error' }));
        }
      }
    };
    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const deleteService = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Are you sure you want to delete this service? This action cannot be undone.',
      onSubmit: async () => {
        const result = await DataService.delete('api/Service/delete/' + id, {});
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'Service deleted successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to delete service', type: 'error' }));
        }
      }
    };
    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const editService = useCallback(async (id: string) => {
    try {
      const response = await DataService.get('api/Service/get/' + id);
      if (response.ok) {
        const res = await response.json();
        const service: IService = res.data;
        setSelectedService(service);
        setOpenAddService(true);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch service details', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading service details', type: 'error' }));
    }
  }, [dispatch]);

  const viewServiceDetails = useCallback((id: string) => {
    navigate(`/services/${id}`);
  }, [navigate]);

  const openAddException = useCallback((id: string) => {
    setSelectedServiceId(id);
    setExceptionDate('');
    setExceptionClosed(true);
    setExceptionWindows([{ start: '09:00', end: '17:00' }]);
    setOpenExceptionDialog(true);
  }, []);

  const handleCloseExceptionDialog = useCallback(() => {
    setOpenExceptionDialog(false);
    setSelectedServiceId('');
    setExceptionDate('');
    setExceptionClosed(true);
    setExceptionWindows([{ start: '09:00', end: '17:00' }]);
  }, []);

  const handleAddTimeWindow = useCallback(() => {
    setExceptionWindows([...exceptionWindows, { start: '09:00', end: '17:00' }]);
  }, [exceptionWindows]);

  const handleRemoveTimeWindow = useCallback((index: number) => {
    setExceptionWindows(exceptionWindows.filter((_, i) => i !== index));
  }, [exceptionWindows]);

  const handleWindowChange = useCallback((index: number, field: 'start' | 'end', value: string) => {
    const updated = [...exceptionWindows];
    updated[index][field] = value;
    setExceptionWindows(updated);
  }, [exceptionWindows]);

  const handleSubmitException = useCallback(async () => {
    if (!exceptionDate) {
      dispatch(showAlert({ message: 'Please select a date', type: 'error' }));
      return;
    }

    const payload = {
      date: exceptionDate,
      closed: exceptionClosed,
      windows: exceptionClosed ? [] : exceptionWindows
    };

    try {
      const result = await DataService.post(
        `api/Service/${selectedServiceId}/exceptions/add`,
        payload
      );
      if (result.ok) {
        dispatch(showAlert({ message: 'Exception added successfully!', type: 'success' }));
        handleCloseExceptionDialog();
        setRefreshKey(prev => prev + 1);
      } else {
        dispatch(showAlert({ message: 'Failed to add exception', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error adding exception', type: 'error' }));
    }
  }, [exceptionDate, exceptionClosed, exceptionWindows, selectedServiceId, dispatch, handleCloseExceptionDialog]);

  const getHeaders = useCallback((language: LanguageTab): EnhancedTableColumn[] => [
    {
      id: 'iconUrl',
      label: 'Icon',
      type: EnhancedTableColumnType.CUSTOM,
      render: (iconUrl: string, row: IServiceListing) => {
        console.log('Rendering icon - iconUrl:', iconUrl, 'row:', row);
        return <ServiceIcon iconUrl={iconUrl} name={row.name} />;
      }
    },
    { id: 'name', label: 'Name', type: EnhancedTableColumnType.TEXT },
    { id: 'description', label: 'Description', type: EnhancedTableColumnType.TEXT },
    {
      id: 'status',
      label: 'Status',
      type: EnhancedTableColumnType.COLORED_CHIP,
      chipColors: {
        Active: { backgroundColor: 'green', color: 'white' },
        Inactive: { backgroundColor: 'red', color: 'white' }
      }
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.ACTION,
      actions: [
        {
          onClick: viewServiceDetails,
          icon: <VisibilityIcon fontSize="small" />,
          label: 'View Details'
        },
        {
          onClick: editService,
          icon: <EditIcon fontSize="small" />,
          label: 'Edit Service'
        },
        {
          onClick: openAddException,
          icon: <EventBusyIcon fontSize="small" />,
          label: 'Add Exception'
        },
        {
          onClick: activate,
          icon: <CheckIcon fontSize="small" />,
          label: 'Activate Service',
          hidden: (r) => r.active === true
        },
        {
          onClick: deactivate,
          icon: <CancelIcon fontSize="small" />,
          label: 'Deactivate Service',
          hidden: (r) => r.active === false
        },
        {
          onClick: deleteService,
          icon: <DeleteIcon fontSize="small" />,
          label: 'Delete Service'
        }
      ]
    }
  ], [activate, deactivate, deleteService, editService, viewServiceDetails, openAddException]);

  const getFilteredData = useCallback((language: LanguageTab): Record<string, IServiceListing> => {
    const result: Record<string, IServiceListing> = {};

    console.log('getFilteredData - allData:', allData);

    allData.forEach((service) => {
      const name = service.name[language];
      const description = service.description[language];

      const listingItem = {
        id: service.id,
        name: name,
        description: description.length > 100
          ? description.substring(0, 100) + '...'
          : description,
        iconUrl: service.iconUrl,
        active: (service as any).active ?? true,
        status: (service as any).active === false ? 'Inactive' : 'Active'
      };

      console.log('Creating listing item:', listingItem);
      result[service.id] = listingItem;
    });

    console.log('getFilteredData result:', result);
    return result;
  }, [allData]);

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await DataService.get('api/Service/all');
      if (response.ok) {
        const res = await response.json();
        const list: IService[] = res.data;
        setAllData(list);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch services', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading services', type: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices, refreshKey]);

  const handleAddSuccess = useCallback(() => {
    setOpenAddService(false);
    setSelectedService(null);
    setRefreshKey(prev => prev + 1);
    dispatch(showAlert({
      message: selectedService ? 'Service updated successfully!' : 'Service added successfully!',
      type: 'success'
    }));
  }, [dispatch, selectedService]);

  const handleClose = useCallback(() => {
    setOpenAddService(false);
    setSelectedService(null);
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: LanguageTab) => {
    setActiveTab(newValue);
  };

  const actions: ITableMenuAction[] = [];

  if (loading) {
    return (
      <Box>
        <Helmet>
          <title>Service List</title>
        </Helmet>
        <MainPageTitle title={'Services List'} />
        <Loader />
      </Box>
    );
  }

  const currentData = getFilteredData(activeTab);
  const currentHeaders = getHeaders(activeTab);

  return (
    <div>
      <Helmet>
        <title>Service List</title>
      </Helmet>
      <MainPageTitle
        title={'Services List'}
        action={{
          title: 'Add Service',
          onClick: () => {
            setOpenAddService(true);
          }
        }}
      />

      <Card sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="service language tabs"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minWidth: 120,
              fontWeight: 600,
              fontSize: '0.95rem',
            },
            '& .Mui-selected': {
              color: '#fff',
            }
          }}
        >
          <Tab label="English" value="en" />
          <Tab label="العربية" value="ar" />
          <Tab label="Français" value="fr" />
        </Tabs>
      </Card>

      <EnhancedTable
        title={`Services (${activeTab.toUpperCase()})`}
        columns={currentHeaders}
        data={currentData}
        actions={actions}
      />
      {openAddService && (
        <AddService
          open={openAddService}
          onSuccess={handleAddSuccess}
          onClose={handleClose}
          service={selectedService}
        />
      )}

      <Dialog open={openExceptionDialog} onClose={handleCloseExceptionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Service Exception</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Date"
              type="date"
              fullWidth
              value={exceptionDate}
              onChange={(e) => setExceptionDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={exceptionClosed}
                  onChange={(e) => setExceptionClosed(e.target.checked)}
                />
              }
              label="Service Closed on this Date"
            />

            {!exceptionClosed && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Time Windows
                </Typography>
                {exceptionWindows.map((window, index) => (
                  <Stack direction="row" spacing={1} key={index} sx={{ mb: 1 }}>
                    <TextField
                      label="Start Time"
                      type="time"
                      value={window.start}
                      onChange={(e) => handleWindowChange(index, 'start', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                    <TextField
                      label="End Time"
                      type="time"
                      value={window.end}
                      onChange={(e) => handleWindowChange(index, 'end', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                    <IconButton
                      onClick={() => handleRemoveTimeWindow(index)}
                      disabled={exceptionWindows.length === 1}
                      color="error"
                      size="small"
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddTimeWindow}
                  variant="outlined"
                  size="small"
                >
                  Add Time Window
                </Button>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExceptionDialog}>Cancel</Button>
          <Button onClick={handleSubmitException} variant="contained">
            Add Exception
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ServiceListing;
